// Damage and death pipeline, extracted from GameBoard.jsx.
// applyDamageToTarget is the single funnel for dealing damage to any
// Avatar / Site / Minion: it routes through Ward, damage replacements,
// Death's Door, and the damage-vs-power death sweep, so every damage
// source (combat, projectiles, effects, manual HUD clicks) behaves
// identically. Death replacements (Undeath, sacrifice-instead) and
// player-death replacements run here too. New damage sources must call
// the GameBoard delegate, never write currentLife directly.
//
// Replacement discovery is pure (engine/automation: findDamage-
// Replacements, findDeathReplacements, findPlayerDeathReplacements);
// this module sequences them against the live board.

import { UI } from '../../../utils/arena/uiSounds';
import { updateLifeHUD } from '../../../utils/game/cardMesh';
import { OP, consumeWardStatus, evaluateCondition, findDamageReplacements, findDeathReplacements, findPlayerDeathReplacements, getEffectiveStatuses, hasActiveWard } from '../../../utils/game/engine/automation';
import { effectiveSubtypes } from '../../../utils/game/engine/automation/cardFilter';
import { spawnFloatingText } from './cardStateOps';
import { attackerHasLethalKill, countsAsMinion, effectiveLife, findDeadMinions, isArtifact, isSubsurface } from '../../../utils/game/engine/core';
import { cardLogLabel } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';

// v1.25. Sweep every minion at 0 effective DEF (currentLife +
// _auraStatDelta ≤ 0) to its controller's Cemetery. Triggered after
// damage application, after manual stat adjustments, and at the end
// of recomputeStaticAuras. The leave path (sendCardToPile) already
// fires Deathrite (v1.15e) before placement, so this stays a pure
// dispatch.
//
// Re-entrancy is safe: sendCardToPile's dedup guard skips cards
// already in the destination pile, and the cascading recomputeStaticAuras
// chain (each death rebuilds auras → may kill more) terminates because
// each iteration removes at least one card from this.meshes.
export function sweepDeadMinions(host) {
  // Manual session: a dead card stays put until a player drags it away.
  if (!host._automationsEnabled()) return;
  if (host._sweepingDeadMinions) return; // re-entry guard
  // Auth: the SERVER decides damage deaths (deathSweep.js, at the commit that
  // dealt the damage). This sweep also runs from mirror folds and relay
  // handlers (recomputeStaticAuras callers), so deciding deaths here made
  // BOTH clients re-send the same death (duplicate MOVE_TO_ZONE + Deathrite,
  // the Rubble-bug class). Subsurface keyword-loss doom is not yet
  // server-owned, so that pass stays. Single-writer on the local side.
  const auth = host._authZonesEnabled?.() === true;
  const findDead = () => (auth ? [] : findDeadMinions(host.meshes));
  const findDoomed = () => {
    const list = findSubsurfaceDoomedMinions(host);
    return auth ? list.filter((c) => host.isCardOnLocalSide?.(c)) : list;
  };
  host._sweepingDeadMinions = true;
  try {
    let safety = 32; // cap chain depth defensively
    let dead = findDead();
    let doomed = findDoomed();
    while ((dead.length > 0 || doomed.length > 0) && safety-- > 0) {
      for (const card of dead) {
        // Already sent this frame? sendCardToPile's own dedup catches
        // it too; this just avoids the log noise.
        if (!host.meshes.has(card.id)) continue;
        // v1.77a. Replacement-effect check. BEFORE sending the
        // dead card to cemetery, query active WOULD_DIE replacements.
        // If any apply, run them (which may heal the card and skip
        // the death entirely). The loop reads the meshes again next
        // iteration, so a healed card stops appearing in `dead`.
        if (host._applyDeathReplacement(card)) continue;
        host.logAction('{card} died', { card: cardLogLabel(card, msg('Card')) });
        // queueDeparture: the pile move commits now (rules timing.
        // the engine sweeps between fight sub-phases), but the visual
        // flight to the cemetery staggers one card at a time and waits
        // for any combat scene to finish, so the player can read who
        // is leaving the battlefield.
        host.sendCardToPile(card, 'Cemetery', false, 'top', { queueDeparture: true });
      }
      // Codex Underground/Underwater: "Whenever a minion without
      // Burrowing/Submerge occupies an underground/underwater location,
      // it immediately dies." This is a CONTINUOUS rule. A minion that
      // loses the keyword while already subsurface (disabled, silenced,
      // or an aura revoking it) dies in place. The forced-bury path
      // handles entry-time deaths; this sweep covers later keyword loss.
      for (const card of doomed) {
        if (!host.meshes.has(card.id)) continue;
        if (host._applyDeathReplacement(card)) continue;
        const minion = cardLogLabel(card, msg('A minion'));
        if (card.subsurface === 'underwater') host.logAction('{card} drowns (lost Submerge)', { card: minion });
        else host.logAction('{card} suffocates (lost Burrowing)', { card: minion });
        host.sendCardToPile(card, 'Cemetery', false, 'top', { queueDeparture: true });
      }
      dead = findDead();
      doomed = findDoomed();
    }
  } finally {
    host._sweepingDeadMinions = false;
  }
}

// Minions sitting below the surface that no longer have the keyword
// keeping them alive there. Uses the EFFECTIVE status set so a disabled
// (collapses to [disabled]), silenced, or aura-revoked minion all read as
// keyword-less. Artifacts persist subsurface regardless (codex names only
// minions); Avatars can never be subsurface so the minion guard suffices.
function findSubsurfaceDoomedMinions(host) {
  const out = [];
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || !isSubsurface(card) || isArtifact(card)) continue;
    if (!countsAsMinion(host._getFullCard(card.cardId) || card)) continue;
    const needed = card.subsurface === 'underwater' ? 'submerge' : 'burrowing';
    if (!getEffectiveStatuses(card).includes(needed)) out.push(card);
  }
  return out;
}

// v1.77b. Apply WOULD_TAKE_DAMAGE replacements right before HP
// mutates. Returns the (possibly reduced) damage amount. Each
// matched replacement entry runs its ops in order: PREVENT_DAMAGE
// zeroes or caps the amount, RETURN_SELF_TO_HAND / BANISH_SELF
// move the watcher off the board, MARK_USED_THIS_TURN gates
// once-per-turn variants like Tufted Turtles.
//
// Condition evaluation runs PER entry. Entries with a condition
// that fails are skipped without consuming their watcher's per-turn
// budget. Matches without a condition (Goswhit Helmet) always fire.
//
// MVP: applies the first matching entry only. Multi-replacement
// priority lands when a catalog interaction surfaces it.
export function applyDamageReplacement(host, target, source, amount, sourceType = null) {
  const getFullCard = host._getFullCard;
  const matches = findDamageReplacements(
    target, source, host.meshes, getFullCard,
    { cellAt: host._cellAtWorld },
  );
  let remaining = amount;
  let redirectedTo = null;
  for (const { watcher, fullWatcher, entry } of matches) {
    if (entry.condition) {
      const bearer = entry.scope?.where === 'self_bearer' ? target : null;
      const ok = evaluateCondition(entry.condition, watcher, fullWatcher, host.meshes,
        { cellAt: host._cellAtWorld, bearer, amount: remaining, sourceType, sourceCard: source });
      if (!ok) continue;
    }
    let prevented = false;
    let redirectTarget = null;
    for (const eff of (entry.effect || [])) {
      // Per-effect condition (lets Makeshift Barricade's break-on-3+
      // be a self-conditional BANISH_SELF inside a single entry).
      if (eff.condition) {
        const ok = evaluateCondition(eff.condition, watcher, fullWatcher, host.meshes,
          { cellAt: host._cellAtWorld, amount: remaining, sourceType, sourceCard: source });
        if (!ok) continue;
      }
      switch (eff.op) {
        case OP.PREVENT_DAMAGE: {
          const cap = eff.args?.amount;
          if (typeof cap === 'number' && cap > 0) {
            remaining = Math.max(0, remaining - cap);
          } else {
            remaining = 0;
          }
          prevented = true;
          break;
        }
        case OP.CAP_DAMAGE: {
          // v1.80. Golden Harp pattern. Clip to at most args.amount.
          // Only marks `prevented` when the cap actually fired (i.e.
          // the damage was above the threshold) so unrelated entries
          // can still process the leftover.
          const ceil = eff.args?.amount;
          if (typeof ceil === 'number' && remaining > ceil) {
            remaining = Math.max(0, ceil);
            prevented = true;
          }
          break;
        }
        case OP.REDIRECT_DAMAGE: {
          // v1.77c. Divert the damage to the watcher itself. We
          // return the redirectTarget via a closure ref so the
          // damage funnel can dispatch the damage to the new card.
          // The original target takes 0.
          redirectTarget = watcher;
          prevented = true;
          break;
        }
        case OP.RETURN_SELF_TO_HAND: {
          // Route to the watcher's OWNER, not the local hand. Combat
          // damage resolves host-authoritatively, so this can fire on
          // a client that doesn't own the watcher. A naive sendToHand
          // would drop it into the attacker's hand (the Mariner's Curse
          // class of bug, here on the damage path).
          host._returnCardToOwnerHand?.(watcher);
          break;
        }
        case OP.RETURN_TARGET_TO_HAND: {
          // Lady Iseult. Return the dying/damaged target to its
          // owner's hand. For WOULD_TAKE_DAMAGE this prevents
          // the hit AND removes the target. (Iseult's actual
          // catalog text is on WOULD_DIE, but this op is shared.)
          host.sendToHand?.(target);
          prevented = true;
          break;
        }
        case OP.BANISH_SELF: {
          host.sendCardToPile(watcher, 'Banished');
          break;
        }
        case OP.MARK_USED_THIS_TURN: {
          const key = eff.args?.key;
          if (key) {
            if (!watcher._abilitiesUsedThisTurn) watcher._abilitiesUsedThisTurn = new Set();
            watcher._abilitiesUsedThisTurn.add(key);
            host._broadcastAbilitiesUsed?.(watcher);
          }
          break;
        }
        case OP.GRANT_STAT_SELF_THIS_TURN: {
          const delta = eff.args?.delta || 0;
          const stat = eff.args?.stat || 'atk';
          if (delta !== 0) host._applyThisTurnStatBuff(watcher, delta, stat);
          break;
        }
        case OP.REMINDER:
          break;
        default:
          console.warn('[damage-replacement] unknown op:', eff.op);
      }
    }
    if (redirectTarget) {
      redirectedTo = redirectTarget;
      host.logAction('{card} takes the damage instead of {target}', {
        card: cardLogLabel(redirectTarget, msg('Bodyguard')), target: cardLogLabel(target, msg('target')),
      });
      break;
    }
    if (prevented) {
      host.logAction('{card} prevents damage to {target}', {
        card: cardLogLabel(watcher, msg('Replacement effect')), target: cardLogLabel(target, msg('target')),
      });
      break;
    }
  }
  // Redirect: route the remaining damage to the new target via a
  // synchronous helper so the original caller still sees `remaining`
  // as 0 (no damage to the original) but the redirected hit lands.
  if (redirectedTo && remaining > 0) {
    const reroute = remaining;
    remaining = 0;
    host._applyRedirectedDamage(redirectedTo, reroute);
  }
  return remaining;
}

// Unified damage-to-target routing. Single recipe for the
// Avatar/Site/Minion dispatch + minion-HP write, consumed by every
// damage source in the engine:
//   - DAMAGE_TARGET / DAMAGE_EACH ops      (spell damage)
//   - `_applyRedirectedDamage`             (retargeted same-event damage)
//   - `_applyWatchTargetDamage`            (watch-trigger damage)
//   - CombatEngine adapter `applyDamage`   (combat strikes + strike-backs)
//
// CRITICAL: minion `currentLife` is NOT clamped at 0. The death-sweep
// (`findDeadMinions`) detects 0-power minions dying from any non-zero
// damage by reading the unclamped value. A `Math.max(0, ...)` here
// would silently mask Faith Incarnate-style minions from dying. Per
// Sorcery codex "Zero": units with 0 power die when they take damage
// (1 or more).
//
// @param {object}  target
// @param {number}  amount
// @param {object}  opts
// @param {string}  opts.sourceLabel       Label for log lines + downstream pipelines.
// @param {boolean} opts.log               Emit "{source}: dealt N to {name}" log line. Default true.
// @param {boolean} opts.sweep             Run `_sweepDeadMinions` after a minion hit. Default true.
// @param {boolean} opts.floatingDamage    Show floating damage numbers on the mesh. Default true.
// @param {object}  opts.lethalAttacker    Attacker card-instance for the Lethal-kill check
//                                         (combat only). When set and `attackerHasLethalKill`
//                                         returns true, the minion is force-killed
//                                         (`currentLife = 0`) regardless of amount.
// @param {object}  opts.source            Source card-instance for WOULD_TAKE_DAMAGE
//                                         replacement conditions (damage_source_match).
// @param {array}   opts.sourceTags        Damage-source tags ('strike' / 'ranged' + elements)
//                                         for damage_source_type conditions.
// @param {boolean} opts.skipWardAndReplacements
//                                         Caller already ran Ward + WOULD_TAKE_DAMAGE at its
//                                         own layer (legacy combat exchange, redirected
//                                         damage, ward-gated target ops). Don't run twice.
// @returns {number} The amount that actually applied after Ward / replacements
//                   (0 when fully absorbed or prevented).
export function applyDamageToTarget(host, target, amount, opts = {}) {
  if (!target || amount <= 0) return 0;
  const {
    sourceLabel = 'Source',
    log = true,
    sweep = true,
    floatingDamage = true,
    lethalAttacker = null,
    source = null,
    sourceTags = null,
    skipWardAndReplacements = false,
  } = opts;

  // Codex order: Ward absorbs the whole hit, then WOULD_TAKE_DAMAGE
  // replacements (Ironclad's "takes 2 less", Goswhit Helmet) adjust
  // what's left, then the HP / life write below.
  if (!skipWardAndReplacements) {
    if (hasActiveWard(target)) {
      host._consumeWard(target);
      if (log) host.logAction('{source}: ward broken on {target}', { source: sourceLabel, target: cardLogLabel(target, msg('target')) });
      return 0;
    }
    amount = host._applyDamageReplacement(target, source, amount, sourceTags);
    if (amount <= 0) return 0;
  }

  // Floating damage number fires for every target type so every
  // damage path (spell / combat / redirect / watch) shows the same
  // visual. The Avatar / Site / Minion branches below handle the
  // gameplay state mutation; this is purely cosmetic feedback.
  if (floatingDamage) {
    const mesh = host.meshes.get(target.id);
    host._spawnFloatingDamage(mesh, amount, '#ef4444');
  }

  if (target.type === 'Avatar') {
    const controller = target.rotated ? 'p2' : 'p1';
    host.playerResources.dealAvatarDamage(controller, amount, sourceLabel);
    if (log) host.logAction('{source}: dealt {n} to {target}', { source: sourceLabel, n: amount, target: cardLogLabel(target) });
    return amount;
  }
  if (target.isSite) {
    const controller = target.rotated ? 'p2' : 'p1';
    host.playerResources.loseLife(controller, amount, msg('{source} hits {target}', { source: sourceLabel, target: target.name || msg('site') }));
    return amount;
  }
  if (target.currentLife === undefined) return 0;

  const next = (target.currentLife ?? 0) - amount;
  target.currentLife = next;
  if (lethalAttacker && attackerHasLethalKill(lethalAttacker, target, amount)) {
    // Drive damage to AT LEAST the effective-life threshold (and a
    // minimum of 1, for 0-power minions) so findDeadMinions sweeps it.
    // Setting currentLife to a flat 0 left aura-buffed defenders
    // (baseLife + auraStatDelta > baseLife) standing. Math.min guards
    // against ever healing a minion already past the threshold.
    const lethalLife = target.baseLife - Math.max(1, effectiveLife(target));
    target.currentLife = Math.min(target.currentLife, lethalLife);
    host.logAction('{card} kills {target} with Lethal', { card: cardLogLabel(lethalAttacker, msg('Attacker')), target: cardLogLabel(target, msg('defender')) });
  }
  const hud = host.lifeHUDs.get(target.id);
  if (hud?.hpSprite) {
    const display = Math.max(0, target.currentLife) + (target._auraStatDelta || 0);
    updateLifeHUD(hud.hpSprite, display, 'hp', display - (target.baseLife ?? display));
  }
  host._broadcastCardStat(target.id, 'hp', target.currentLife);
  if (log) host.logAction('{source}: dealt {n} to {target}', { source: sourceLabel, n: amount, target: cardLogLabel(target, msg('target')) });
  if (sweep) host._sweepDeadMinions?.();
  return amount;
}

// Apply damage to a redirect target. Retargeted same-event damage:
// no strike-back, no triggers, no extra log (the redirect log line
// is already emitted by the caller). The sweep is left to the
// combat pipeline that owns the originating damage event. A replaced
// event is not replaced again (codex), so Ward / WOULD_TAKE_DAMAGE
// are skipped on the redirected hit.
export function applyRedirectedDamage(host, target, amount) {
  // Mark the absorbing card so an on_death gate can tell its death came from
  // redirected damage (Sir Gawain "If he dies from damage taken this way,
  // draw a card."). Cleared at the end-phase heal with the damage itself.
  if (target) target._tookRedirectedDamage = true;
  host._applyDamageToTarget(target, amount, {
    sourceLabel: 'redirect',
    log: false,
    sweep: false,
    floatingDamage: true,
    skipWardAndReplacements: true,
  });
}

// v1.77e. Tags describing a damage event. Replacement-effect
// `damage_source_type` conditions check `any` against this list.
// Includes the kind ('strike' / 'ranged' / 'spell') plus every
// lowercase element on the source card, so a fire creature's strike
// matches Askelon Phoenix's fire-damage gate.
export function buildDamageSourceTags(host, sourceCard, isProjectile) {
  const tags = [isProjectile ? 'ranged' : 'strike'];
  if (sourceCard?.elements) {
    const els = Array.isArray(sourceCard.elements)
      ? sourceCard.elements
      : String(sourceCard.elements).split(/[,;\s]+/);
    for (const e of els) {
      const s = String(e || '').trim().toLowerCase();
      if (s) tags.push(s);
    }
  }
  return tags;
}

// v1.77f. Try every active "if you would die" replacement controlled
// by the dying player. Returns true if one fired successfully and
// the death is averted; false if none apply (caller proceeds with
// game-over). The first replacement that resolves wins. Codex says
// the controller picks, but in this auto-resolve pass there's almost
// never more than one source-of-truth Altar in play. UI for an
// explicit "use Altar?" yes/no prompt is deferred.
export function attemptPlayerDeathReplacement(host, controller, sourceLabel) {
  const getFullCard = host._getFullCard;
  const matches = findPlayerDeathReplacements(controller, host.meshes, getFullCard);
  for (const { watcher, fullWatcher, entry } of matches) {
    if (entry.condition) {
      const ok = evaluateCondition(entry.condition, watcher, fullWatcher, host.meshes,
        { cellAt: host._cellAtWorld });
      if (!ok) continue;
    }
    let prevented = false;
    let needsCost = false;
    let costPaid = true;
    for (const eff of (entry.effect || [])) {
      switch (eff.op) {
        case OP.SACRIFICE_HERE: {
          // v1.83. Codex Sacrifice: "sacrifice effects supersede
          // all destruction prevention effects." sendCardToPile
          // bypasses both Ward and WOULD_DIE replacements (the
          // latter only run from _sweepDeadMinions on HP-0 deaths,
          // never from explicit pile moves), so a sacrificed
          // minion correctly dies through Ward / Lady Iseult /
          // Gilded Aegis. Deathrite still fires via
          // _fireDeathriteOnLeave per codex.
          needsCost = true;
          const victim = host._pickSacrificeHere(watcher, eff.args?.filter);
          if (!victim) { costPaid = false; break; }
          host.logAction("{card}: sacrificing {victim} to prevent {who}'s death", {
            card: cardLogLabel(watcher, msg('Altar')),
            victim: cardLogLabel(victim, msg('a minion')),
            who: controller === 'p1' ? msg('Player 1') : msg('Player 2'),
          });
          host.sendCardToPile(victim, 'Cemetery');
          break;
        }
        case OP.PREVENT_DEATH:
          prevented = true;
          break;
        case OP.MARK_USED_THIS_GAME: {
          const key = eff.args?.key;
          if (key) {
            if (!watcher._abilitiesUsedThisGame) watcher._abilitiesUsedThisGame = new Set();
            watcher._abilitiesUsedThisGame.add(key);
            host._broadcastAbilitiesUsed?.(watcher);
          }
          break;
        }
        case OP.REMINDER:
          break;
        default:
          console.warn('[player-death-replacement] unknown op:', eff.op);
      }
      if (needsCost && !costPaid) break;
    }
    if (needsCost && !costPaid) continue;
    if (prevented) {
      host.logAction('{card} averts a death blow ({reason})', {
        card: cardLogLabel(watcher, msg('Replacement effect')), reason: sourceLabel || msg('damage'),
      });
      return true;
    }
  }
  return false;
}

// v1.77f. Pick the first minion sharing a cell with `source` (here
// scope) optionally matching `filter`. Used by Altar of Malachai's
// sacrifice cost. Returns null if no eligible target exists.
export function pickSacrificeHere(host, source, filter = null) {
  const sourceMesh = host.meshes.get(source.id);
  if (!sourceMesh) return null;
  const sCell = host._cellAtWorld(sourceMesh.position.x, sourceMesh.position.z);
  if (!sCell) return null;
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || c.id === source.id) continue;
    if (!countsAsMinion(c)) continue;
    if (!!c.rotated !== !!source.rotated) continue;
    const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cell || cell.col !== sCell.col || cell.row !== sCell.row) continue;
    if (filter?.subtype) {
      // effectiveSubtypes so a Corruptor-transformed unit is targetable
      // by its current identity (Beast → Monster), not its printed one.
      const subs = effectiveSubtypes(c);
      const wanted = (Array.isArray(filter.subtype) ? filter.subtype : [filter.subtype])
        .map((s) => String(s).toLowerCase());
      if (!wanted.some((w) => subs.includes(w))) continue;
    }
    return c;
  }
  return null;
}

// v1.77a. Apply WOULD_DIE replacements before sending a card to
// cemetery. Returns true when a replacement fired and the death
// was averted (so the sweep skips sending to cemetery). Replacement
// effects do NOT use the storyline per codex. They resolve
// synchronously inline with the dying event.
//
// MVP today supports one replacement per dying card (first match
// wins). Multi-replacement priority (controller chooses) lands when
// a real two-effect interaction surfaces in the catalog.
export function applyDeathReplacement(host, dyingCard) {
  const getFullCard = host._getFullCard;
  const matches = findDeathReplacements(
    dyingCard, host.meshes, getFullCard,
    { cellAt: host._cellAtWorld },
  );
  if (matches.length === 0) return false;
  const { watcher, entry } = matches[0];
  let prevented = false;
  for (const eff of (entry.effect || [])) {
    switch (eff.op) {
      case OP.PREVENT_DEATH: {
        prevented = true;
        break;
      }
      case OP.HEAL_FULL: {
        // Target defaults to the dying card. Future args.target
        // can route healing elsewhere; not currently exercised.
        const target = dyingCard;
        if (target && target.baseLife != null) {
          target.currentLife = target.baseLife;
          host._refreshLifeHUD(target);
          host._broadcastCardStat(target.id, 'hp', target.currentLife);
        }
        break;
      }
      case OP.BANISH_SELF: {
        // The WATCHER (e.g. Gilded Aegis) gets banished, not the
        // dying card. Equipped artifacts banish from the bearer's
        // equipment array via the existing leave-play path.
        host.sendCardToPile(watcher, 'Banished');
        break;
      }
      case OP.RETURN_SELF_TO_HAND: {
        // Goswhit Helmet pattern. The watcher returns to its OWNER's
        // hand (handles equipped vs board). Death sweeps resolve
        // host-authoritatively, so a naive local sendToHand would land
        // the watcher in the wrong player's hand when fired on the
        // non-owner's client.
        host._returnCardToOwnerHand?.(watcher);
        break;
      }
      case OP.RETURN_TARGET_TO_HAND: {
        // v1.77c. Lady Iseult: nearby ally dying → return to hand
        // instead of cemetery. The dying card has currentLife=0 at
        // this point; sendToHand restores it (handles hp reset via
        // the leave-play path).
        host.sendToHand?.(dyingCard);
        prevented = true;
        break;
      }
      case OP.REMINDER:
        break;
      default:
        console.warn('[death-replacement] unknown op:', eff.op);
    }
  }
  if (prevented) {
    host.logAction('{card} saves {target} from death', {
      card: cardLogLabel(watcher, msg('Replacement effect')), target: cardLogLabel(dyingCard, msg('a minion')),
    });
  }
  return prevented;
}

// Break a ward. Pure status mutation lives in wards.js; GameBoard
// owns the badge rebuild, network broadcast, and aura recompute
// (Faith Incarnate's per-ward power scaling needs to refresh on the
// same frame).
export function consumeWard(host, card) {
  if (!consumeWardStatus(card)) return;
  // A warded hit otherwise resolves with NO visible change. Which
  // reads as a bug, not a save. Pop the beat that says what happened.
  const mesh = host.meshes.get(card.id);
  if (mesh) spawnFloatingText(host, mesh, 'WARDED', '#7ec8ff');
  host.rebuildStatusBadges(card.id);
  host._broadcastCardStatus(card.id, 'ward', false);
  host.recomputeStaticAuras();
}
